/**
 * Profile API Service
 * Wraps the /api/auth/profile family of endpoints (identity, avatar,
 * phone verification, password change, sessions, login history).
 */

import { request } from './apiClient';

const profileService = {
    /**
     * Get the full profile for the authenticated user
     * @returns {Promise<Object>}
     */
    getProfile: async () => {
        const data = await request('/auth/profile');
        return data.data;
    },

    /**
     * Update identity/contact/rental-readiness fields. Omitted fields keep
     * their current value server-side. Note the request body field is
     * `phone`, while the profile response field is `phoneNumber`.
     * @param {Object} updates
     * @returns {Promise<Object>} the full updated profile
     */
    updateProfile: async (updates) => {
        const data = await request('/auth/profile', { method: 'PUT', body: updates });
        return data.data;
    },

    /**
     * Upload/replace the profile avatar
     * @param {File} file
     * @returns {Promise<{message: string, profileImage: string}>}
     */
    uploadAvatar: async (file) => {
        const formData = new FormData();
        formData.append('avatar', file);
        const data = await request('/auth/profile/avatar', { method: 'POST', body: formData, multipart: true });
        return data.data;
    },

    /**
     * Change the account password
     * @param {Object} params
     * @param {string} params.currentPassword
     * @param {string} params.newPassword
     * @param {string} params.confirmPassword
     * @returns {Promise<Object>}
     */
    changePassword: async ({ currentPassword, newPassword, confirmPassword }) => {
        const data = await request('/auth/change-password', {
            method: 'POST',
            body: { currentPassword, newPassword, confirmPassword }
        });
        return data.data;
    },

    /**
     * Request a phone verification OTP, sent to the phone already on file
     * @returns {Promise<{message: string, expiresInMinutes: number}>}
     */
    requestPhoneVerification: async () => {
        const data = await request('/auth/verify-phone/request', { method: 'POST' });
        return data.data;
    },

    /**
     * Confirm a phone verification OTP
     * @param {string} code
     * @returns {Promise<Object>} the full updated profile (phoneVerified: true)
     */
    confirmPhoneVerification: async (code) => {
        const data = await request('/auth/verify-phone/confirm', { method: 'POST', body: { code } });
        return data.data;
    },

    /**
     * List active sessions/devices
     * @returns {Promise<Array>}
     */
    getSessions: async () => {
        const data = await request('/auth/sessions');
        return data.data?.sessions || [];
    },

    /**
     * Revoke a session by id
     * @param {number} sessionId
     * @returns {Promise<Object>}
     */
    revokeSession: async (sessionId) => {
        const data = await request(`/auth/sessions/${sessionId}`, { method: 'DELETE' });
        return data.data;
    },

    /**
     * Get recent login history
     * @param {number} [limit=20]
     * @returns {Promise<Array>}
     */
    getLoginHistory: async (limit = 20) => {
        const data = await request(`/auth/login-history?limit=${limit}`);
        return data.data?.history || [];
    }
};

export default profileService;
